import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { MinesService } from '../mines.service';
import { SessionService } from '@core/services/session.service';
import { ClipboardService } from '@core/services/clipboard.service';
import { CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GameIconComponent } from '../../../core/components/game-icon/game-icon.component';
import { InputOptionsComponent } from '../../../core/components/input-options/input-options.component';
import { BalanceIconComponent } from '../../../core/components/balance-icon/balance-icon.component';
import { FairnessButtonComponent } from '../../../core/components/fairness-button/fairness-button.component';
import { fadeInAnimation } from '@core/utils/animations.util';
import { OldSliderComponent } from '@core/components/old-slider/old-slider.component';

@Component({
  selector: 'app-mines-bet-section',
  templateUrl: './mines-bet-section.component.html',
  styleUrls: ['./mines-bet-section.component.scss'],
  host: {
    class: 'box-black-third rounded-6',
  },
  imports: [
    CurrencyPipe,
    FormsModule,
    GameIconComponent,
    InputOptionsComponent,
    BalanceIconComponent,
    FairnessButtonComponent,
    OldSliderComponent,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeInAnimation],
})
export class MinesBetSectionComponent {
  betInputOptions = [
    {
      label: '1/2',
      fn: () => this.setBet('half'),
    },
    {
      label: '2x',
      fn: () => this.setBet('double'),
    },
    {
      label: 'Max',
      fn: () => this.setBet('max'),
    },
  ];

  minesInputOptions = [
    {
      label: '-',
      fn: () => this.setMines('minus'),
    },
    {
      label: '+',
      fn: () => this.setMines('plus'),
    },
    {
      label: 'Min',
      fn: () => this.setMines('min'),
    },
    {
      label: 'Max',
      fn: () => this.setMines('max'),
    },
  ];

  betButtons = [
    {
      label: '+1',
      fn: () => this.setBet(1),
    },
    {
      label: '+10',
      fn: () => this.setBet(10),
    },
    {
      label: '+25',
      fn: () => this.setBet(25),
    },
    {
      label: '+100',
      fn: () => this.setBet(100),
    },
  ];

  constructor(
    public minesService: MinesService,
    public session: SessionService,
    public clipboardService: ClipboardService,
    public sessionService: SessionService,
    private toastrService: ToastrService
  ) {}

  setBet(bet: string | number) {
    this.minesService.amount.update((prev) => {
      let value: number = prev;

      if (typeof bet === 'number') {
        value = prev + bet;
      } else {
        switch (bet) {
          case 'max':
            value = Math.min(
              this.session.currentBalance() ?? 0,
              this.minesService.MAX_BET
            );
            break;

          case 'half':
            value = Math.max(prev / 2, this.minesService.MIN_BET);
            break;

          case 'double':
            value = Math.min(prev * 2, this.minesService.MAX_BET);
            break;
        }
      }

      value = Math.min(value, this.minesService.MAX_BET);
      value = Math.max(value, this.minesService.MIN_BET);

      return Math.round(value * 100) / 100;
    });
  }

  private setMines(type: 'minus' | 'plus' | 'min' | 'max') {
    const MIN = 1;
    const MAX = 24;
    this.minesService.mines.update((prev) => {
      let newValue = prev;

      switch (type) {
        case 'minus':
          newValue = prev - 1;
          break;
        case 'plus':
          newValue = prev + 1;
          break;
        case 'min':
          newValue = MIN;
          break;
        case 'max':
          newValue = MAX;
          break;
      }

      if (newValue < MIN) {
        this.toastrService.error(
          `You can only play with at least ${MIN} mine!`
        );
        return MIN;
      }

      if (newValue > MAX) {
        this.toastrService.error(`You can only play up to ${MAX} mines!`);
        return MAX;
      }

      return newValue;
    });
  }

  handleStartGame() {
    if (this.minesService.currentGame()?.status) {
      this.minesService.resetBoard();
    }

    this.minesService.placeBet();
  }
}
